import {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import dataDogLogger from '../../../lib/datadog';
import CardContainer from '../components/cardContainer';
import {
    Box, Slide, 
    Typography,
    useTheme
} from '@mui/material';
import ProgressBar from '../components/progressBar';
import {
    makeStyles 
} from '@mui/styles';
import Card from '../components/card';
import Link from 'next/link';
import {
    useCookies 
} from 'react-cookie';
import useAppUserAgent from '../../core/use-app-user-agent';
import {
    ADJUST_EVENTS, AdjustEventPayload, trackAdjustEvent 
} from '../../../lib/adjust';
import {
    Data, get, post, put 
} from '../../../networking';
import {
    validateEmail 
} from '../../../lib/utils';
import Phone from '../../authentication/phone';
import SessionContext from '../../../contexts/session';
import {
    ampLogEvent 
} from '../../../lib/amplitude';
import Code from '../../authentication/code';
import {
    refreshUserAndStoreIn 
} from '../../../logic/auth';
import {
    gaEvent 
} from '../../../lib/gtag';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';
import SunnysideButton, {
    ButtonSizes, ButtonVariants 
} from '../../../components_sunnyside/button';

async function verifyEmail(email: string): Promise<Data> {
    return await get(`auth/verify?email=${encodeURIComponent(email)}`);
}

async function updateEmail(email: string): Promise<Data> {
    return await post('users/set-email', {
        email 
    });
}

async function putCurrentUser(payload: Data): Promise<Data> {
    return await put('users/current-user', payload);
}

type Props = {
    position: number,
    sectionKey: string,
    backJump?: number
};

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const skippyEnvironment = new Set(['development', 'staging']);

export const InputsScreen = ({
    position,
    sectionKey,
    backJump
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);
    const nameAnswer = useMemo(() => {
        return formContext.answers?.your_name;
    }, [formContext.answers]);
    const emailAnswer = useMemo(() => {
        return formContext.answers?.email;
    }, [formContext.answers]);

    const text = useRef(null);
    const [textHeight, setTextHeight] = useState(0);
    useEffect(() => {
        if (text.current && !textHeight) {
            setTextHeight(text.current.clientHeight);
        }
    }, [text.current]);
    const useStyles = useMemo(() => (makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        slideContainer: {
            height: `${textHeight}px`
        },
        textContainer: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '12px',
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '12px'
        },
        hidden: {
            position: 'absolute',
            opacity: 0,
            width: 'calc(100% - 48px)'
        },
        progress: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '8px',
            margin: '8px 0'
        },
        circle: {
            width: '8px',
            height: '8px',
            borderRadius: '50%'
        },
        disclaimerBox: {
            display: 'grid',
            gridTemplateColumns: '16px 1fr',
            columnGap: '12px',
            alignItems: 'center'
        },
        fadeIn: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '12px',
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 0.5s',
        },
        label: {
            lineHeight: '24px',
            fontWeight: TypographyWeights.SEMI_BOLD,
            margin: '0 0 4px'
        },
        input: {
            fontSize: '16px',
            padding: '12px 16px',
            borderRadius: '12px',
            border: `1px solid ${theme.palette.neutral.light}`,
            outline: 'none',
            width: '100%',
            ['&:invalid']: {
                border: `1px solid ${theme.palette.error.main}`
            }
        },
        emailError: {
            textAlign: 'center',
            color: theme.palette.error.main
        },
        loginLink: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: TypographyWeights.REGULAR,
            color: theme.palette.primary.main,
            margin: '0'
        },
        phoneContainer: {
            margin: '0 auto',
            [theme.breakpoints.up('lg')]: {
                width: '60%'
            },
            height: '100%',
            width: '100%',
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 0s'
        },
        cta: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.primary.main,
            cursor: 'pointer'
        },
        skippy: {
            backgroundColor: theme.palette.error.main,
            margin: '0 auto'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        }
    }))), [textHeight]);

    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const session = useContext(SessionContext);
    const appUserAgent = useAppUserAgent();

    const [step, setStep] = useState(formContext.screenDirection === 'right' ? 2 : 0);
    const [slide, setSlide] = useState(true);
    const [direction, setDirection] = useState<'left' | 'right'>('left');
    const [startTime, setStartTime] = useState(formContext.startTime);

    const [name, setName] = useState(nameAnswer || '');
    const [showNameButton, setShowNameButton] = useState(name.length > 0);
    const nameInputBox = useRef(null);

    const [email, setEmail] = useState(emailAnswer || '');
    const [emailError, setEmailError] = useState('');
    const [showLoginButton, setShowLoginButton] = useState(false);
    const [showEmailButton, setShowEmailButton] = useState(email.length > 0);
    const emailInputBox = useRef(null);

    useEffect(() => {
        switch (step) {
        case 0: {
            formContext.fireTrackingEvents(formContext, 'your_name', cookies);
            break;
        }
        case 1: {
            formContext.fireTrackingEvents(formContext, 'email entry', cookies);
            break;
        }
        case 2: {
            formContext.fireTrackingEvents(formContext, 'phone entry', cookies);
            gaEvent({
                action: 'Page',
                category: 'Land',
                label: 'signup: landed on phone entry',
                value: '',
            });
            break;
        }
        default: {
            break;
        }
        }
    }, [step]);

    const advanceScreen = () => {
        if (step === 3) {
            formContext.goNext();
            return;
        }
        setDirection('right');
        setStep(step + 1);
        setSlide(false);
        setStartTime(new Date().getTime());
    };

    const goBackScreen = () => {
        if (step === 0) {
            formContext.goBack(backJump);
            return;
        }
        setDirection('left');
        setStep(step - 1);
        setSlide(false);
        setStartTime(null);
    };

    const SUBTITLES = useMemo(() => ([
        <Typography
            key={0}
            variant={TypographyVariants.H6}
            dangerouslySetInnerHTML={{
                __html: 'Let\'s start with your <span style="font-weight:700;">first name</span>, or nickname if you\'d prefer to stay anonymous.' 
            }}
        />,
        <Typography
            key={1}
            variant={TypographyVariants.H6}
            dangerouslySetInnerHTML={{
                __html: 'Next, please enter your <span style="font-weight:700;">email</span>. We use your email to share weekly reports that help you track your progress toward your goals.' 
            }}
        />,
        <Typography
            key={2}
            variant={TypographyVariants.H6}
            dangerouslySetInnerHTML={{
                __html: 'Lastly, enter your <span style="font-weight:700;">phone number</span>. We use your phone number for coaching and tracking reminders, and for a secure login experience.' 
            }}
        />,
        (
            <Box key={3}>
                <Typography
                    variant={TypographyVariants.H6}
                >
                    {`We sent the code to ${session?.user?.phone}`}
                </Typography>
                <Typography
                    variant={TypographyVariants.H6}
                    style={{
                        color: theme.palette.primary.main,
                        cursor: 'pointer' 
                    }}
                    onClick={goBackScreen}
                >
                    Update phone number
                </Typography>
            </Box>
        ),
    ]), [session?.user?.phone, goBackScreen, classes]);

    const [subtitle, setSubtitle] = useState(SUBTITLES[step]);

    useEffect(() => {
        if (slide) return;

        setTimeout(() => {
            setSubtitle(SUBTITLES[step]);
        }, 500);
    }, [slide]);

    useEffect(() => {
        setSlide(true);
        setDirection(direction === 'right' ? 'left' : 'right');
    }, [subtitle]);

    const skippySkipsName = () => {
        formContext.setAnswers({
            your_name: 'Skippy'
        });

        advanceScreen();
    };

    useEffect(() => {
        if (!startTime || step === 3) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen ${step === 0 ? 'your_name' : step === 1 ? 'email entry' : step === 2 ? 'phone entry' : 'code input'} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: step === 0 ? 'your_name' : step === 1 ? 'email entry' : step === 2 ? 'phone entry' : 'code input',
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    useEffect(() => {
        if (!nameInputBox.current) return;

        setTimeout(() => {
            nameInputBox.current.focus();
        }, 1000);
    }, [nameInputBox.current]);

    const onNameButtonClick = () => {
        formContext.setAnswers({
            your_name: name 
        });

        if (nameInputBox.current) nameInputBox.current.blur();

        setTimeout(() => {
            advanceScreen();
        }, 50);
    };

    const onNameChange = (e) => {
        setName(e.target.value);
        if (e.target.value && e.target?.checkValidity()) {
            setShowNameButton(true);
        } else {
            setShowNameButton(false);
        }
    };

    const onNameKeyPress = (e) => {
        if (!name) return;

        if (e.key === 'Enter') onNameButtonClick();
    };

    useEffect(() => {
        if (!emailInputBox.current) return;

        const timeout = setTimeout(() => {
            emailInputBox.current.focus();
        }, 1000);

        return () => clearTimeout(timeout);
    }, [emailInputBox.current]);

    const onEmailButtonClick = (e: MouseEvent) => {
        if (e) {
            e.preventDefault();
        }

        handleEmailValidation();
    };

    const handleEmailValidation = async () => {
        if (!email) {
            setEmailError('Your email is required');
            return;
        }

        const isValidEmail = await verifyEmail(email);

        if (!validateEmail(email)) {
            setEmailError('Please input a valid email');
            setShowLoginButton(false);
        } else if (!isValidEmail.isValid) {
            setEmailError('Email already exists');
            setShowLoginButton(true);
        } else if (validateEmail(email)) {
            const formattedEmail = email.trim();
            await updateEmail(formattedEmail);

            formContext.setAnswers({
                email: formattedEmail 
            });

            if (emailInputBox.current) emailInputBox.current.blur();

            if (cookies?.ss_app === 'true' && (cookies?.ss_adid || cookies?.ss_idfv || cookies?.ss_gps_adid)) {
                const event_token = ADJUST_EVENTS[cookies.ss_app_device]?.email;
                if (event_token) {
                    const payload: AdjustEventPayload = {
                        event_token,
                    };

                    if (cookies.ss_adid) payload.adid = cookies.ss_adid;
                    if (cookies.ss_idfv) payload.idfv = cookies.ss_idfv;
                    if (cookies.ss_gps_adid) payload.gps_adid = cookies.ss_gps_adid;

                    trackAdjustEvent(payload, cookies.ss_app_device);
                }
            }

            setTimeout(() => {
                advanceScreen();
            }, 50);

            setEmailError('');
            setShowLoginButton(false);
        } else {
            setEmailError('');
            setShowLoginButton(false);
        }
    };

    const onEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value.trim());
        setEmailError('');
        setShowLoginButton(false);
        if (value.trim().length > 0 && !showEmailButton) {
            setShowEmailButton(true);
        }
        else if (value.trim().length === 0 && showEmailButton) {
            setShowEmailButton(false);
        }
    };

    const duplicateEmailUrl = () => {
        if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
            return `/app-bridge?redirect=${encodeURIComponent(
                `/logout?url=${encodeURIComponent('login/email')}`
            )}&web_app=true`;
        }
        return `/logout?url=${encodeURIComponent('login/email')}`;
    };

    const onKeyPress = (e) => {
        if (!email || emailError) return;

        if (e.key === 'Enter') handleEmailValidation();
    };

    const handlePhoneChange = (phoneNumber) => {
        setStartTime(new Date().getTime());
        if (session.user.phone !== phoneNumber) {
            putCurrentUser({
                phone: phoneNumber 
            });
            session.setUser({
                phone: phoneNumber 
            });
        }
        ampLogEvent(
            'signup: authorized phone number in flow',
            {
                formId: formContext.formId
            },
            cookies
        );
        advanceScreen();
    };

    const handleOnChange = async (validated: boolean) => {
        if (validated) {
            await refreshUserAndStoreIn(session);
            formContext.goNext();
        }
    };

    const showButton = useMemo(() => {
        if (step === 0) {
            return showNameButton;
        } else if (step === 1) {
            return showEmailButton;
        } else if (step === 2) {
            return false;
        }
    }, [step, showNameButton, showEmailButton]);

    const onClick = useMemo(() => {
        if (step === 0) {
            return onNameButtonClick;
        } else if (step === 1) {
            return onEmailButtonClick;
        } else if (step === 2) {
            return null;
        }
    }, [step, onNameButtonClick, onEmailButtonClick]);

    return (
        <CardContainer
            sectionKey={sectionKey}
            backJump={backJump}
            goBack={goBackScreen}
            goNext={showButton && onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={sectionKey}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Typography
                            variant={TypographyVariants.H3}
                            dangerouslySetInnerHTML={{
                                __html: 'Before we jump into your plan, <span style="font-weight:700;">let\'s set up your profile.</span>' 
                            }}
                        />
                        <Box className={classes.progress}>
                            <Box
                                className={classes.circle}
                                style={{
                                    backgroundColor: step === 0 ? section?.colors?.main : section?.colors?.background 
                                }}
                            />
                            <Box
                                className={classes.circle}
                                style={{
                                    backgroundColor: step === 1 ? section?.colors?.main : section?.colors?.background 
                                }}
                            />
                            <Box
                                className={classes.circle}
                                style={{
                                    backgroundColor: step === 2 ? section?.colors?.main : section?.colors?.background 
                                }}
                            />
                            <Box
                                className={classes.circle}
                                style={{
                                    backgroundColor: step === 3 ? section?.colors?.main : section?.colors?.background 
                                }}
                            />
                        </Box>
                        <Box className={classes.slideContainer}>
                            <Slide
                                in={slide}
                                direction={direction}
                                timeout={250}
                                mountOnEnter
                                unmountOnExit
                            >
                                <Box className={classes.textContainer}>
                                    <Box
                                        className={classes.hidden}
                                        ref={text}
                                    >
                                        {SUBTITLES[2]}
                                    </Box>
                                    {subtitle}
                                </Box>
                            </Slide>
                        </Box>
                        {step === 0 && (
                            <Box className={classes.fadeIn}>
                                <Box>
                                    <Typography
                                        variant={TypographyVariants.H6}
                                        className={classes.label}
                                    >
                                        {'Your first name (or nickname)'}
                                    </Typography>
                                    <input
                                        ref={nameInputBox}
                                        autoCorrect={'off'}
                                        spellCheck={false}
                                        autoComplete={'given-name'}
                                        name={'name'}
                                        type={'text'}
                                        aria-invalid={'false'}
                                        className={classes.input}
                                        placeholder={'Enter your name'}
                                        onChange={onNameChange}
                                        onKeyPress={onNameKeyPress}
                                        value={name}
                                    />
                                </Box>
                                <Box className={classes.disclaimerBox}>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M3.258 7.67148C2.84115 7.67148 2.50322 8.00941 2.50322 8.42626V13.8454C2.50322 14.2622 2.84115 14.6001 3.258 14.6001H12.7414C13.1583 14.6001 13.4962 14.2622 13.4962 13.8454V8.42626C13.4962 8.00941 13.1583 7.67148 12.7414 7.67148H3.258ZM1.30322 8.42626C1.30322 7.34667 2.17841 6.47148 3.258 6.47148H12.7414C13.821 6.47148 14.6962 7.34667 14.6962 8.42626V13.8454C14.6962 14.925 13.821 15.8001 12.7414 15.8001H3.258C2.17841 15.8001 1.30322 14.925 1.30322 13.8454V8.42626Z"
                                            fill={theme.palette.neutral.main}
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.99784 10.152C7.45457 10.152 7.01416 10.5924 7.01416 11.1356C7.01416 11.6789 7.45457 12.1193 7.99784 12.1193C8.54111 12.1193 8.98152 11.6789 8.98152 11.1356C8.98152 10.5924 8.54111 10.152 7.99784 10.152ZM5.81416 11.1356C5.81416 9.92962 6.79183 8.95195 7.99784 8.95195C9.20386 8.95195 10.1815 9.92962 10.1815 11.1356C10.1815 12.3416 9.20386 13.3193 7.99784 13.3193C6.79183 13.3193 5.81416 12.3416 5.81416 11.1356Z"
                                            fill={theme.palette.neutral.main}
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.00396 0.199774H8.07499C9.12333 0.210522 10.1245 0.637229 10.8583 1.386C11.5913 2.13404 11.9976 3.14234 11.988 4.18957V4.8641C11.988 5.19547 11.7194 5.4641 11.388 5.4641C11.0567 5.4641 10.788 5.19547 10.788 4.8641V4.18074C10.7953 3.45058 10.5123 2.74743 10.0012 2.22592C9.49082 1.7051 8.7947 1.40802 8.06561 1.39977H7.99513C7.26497 1.39251 6.56182 1.67551 6.0403 2.18659C5.51949 2.697 5.22241 3.39311 5.21416 4.1222V4.8641C5.21416 5.19547 4.94553 5.4641 4.61416 5.4641C4.28279 5.4641 4.01416 5.19547 4.01416 4.8641V4.11282C4.02491 3.06449 4.45162 2.06334 5.20039 1.32954C5.94843 0.596464 6.95673 0.190176 8.00396 0.199774Z"
                                            fill={theme.palette.neutral.main}
                                        />
                                    </svg>
                                    <Typography
                                        variant={TypographyVariants.VERY_SMALL}
                                    >
                                        {'We\'re committed to your privacy. We\'ll never share your name with any third parties.'}
                                    </Typography>
                                </Box>
                                {showNameButton && (skippyEnvironment.has(ENVIRONMENT)) && (
                                    <SunnysideButton
                                        buttonVariant={ButtonVariants.PRIMARY}
                                        buttonSize={ButtonSizes.LARGE}
                                        onClick={skippySkipsName}
                                        className={classes.skippy}
                                    >
                                        <Typography
                                            style={{
                                                fontWeight: TypographyWeights.BOLD,
                                                color: theme.palette.white
                                            }}
                                        >
                                            SKIPPY
                                        </Typography>
                                    </SunnysideButton>
                                )}
                            </Box>
                        )}
                        {step === 1 && (
                            <Box className={classes.fadeIn}>
                                <Box>
                                    <Typography
                                        variant={TypographyVariants.H6}
                                        className={classes.label}
                                    >
                                        Your Email Address
                                    </Typography>
                                    <input
                                        ref={emailInputBox}
                                        autoCorrect={'off'}
                                        spellCheck={false}
                                        autoComplete={'email'}
                                        autoCapitalize={'off'}
                                        name={'email'}
                                        required
                                        type={'text'}
                                        aria-invalid={'false'}
                                        onKeyPress={onKeyPress}
                                        className={classes.input}
                                        style={{
                                            border: emailError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.neutral.light}` 
                                        }}
                                        placeholder={'Enter your email'}
                                        onChange={onEmailChange}
                                        value={email}
                                    />
                                </Box>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.emailError}
                                >
                                    {emailError}
                                </Typography>
                                {showLoginButton && (
                                    <Link
                                        href={duplicateEmailUrl()}
                                        passHref
                                        prefetch={false}
                                    >
                                        <a
                                            className={classes.loginLink}
                                        >
                                            Please log in instead
                                        </a>
                                    </Link>
                                )}
                            </Box>
                        )}
                        {step === 2 && (
                            <Box className={classes.phoneContainer}>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.label}
                                >
                                    Your Phone Number
                                </Typography>
                                <Phone
                                    mode='register'
                                    onChange={handlePhoneChange}
                                    initialPhone={session?.user?.phone || ''}
                                    isCustom
                                />
                            </Box>
                        )}
                        {step === 3 && (
                            <Box className={classes.phoneContainer}>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.label}
                                >
                                    Verification Code
                                </Typography>
                                <Code
                                    submitButtonLabel="See my plan"
                                    mode="register"
                                    autoSubmit={false}
                                    onChange={handleOnChange}
                                    goBack={goBackScreen}
                                    isCustom
                                    hideText
                                    formId={formContext.formId}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default InputsScreen;
